import type { DragEvent } from 'react'
import type { AcaoDisponivel, ResumoGmud, StatusGmud } from '../../../types/gmud'

/**
 * O arraste PROPÕE uma transição; quem a executa continua sendo `useAcionarTransicao`.
 *
 * Nada aqui é uma segunda máquina de estados. Não há mapa de coluna para comando, e não pode
 * haver: um mapa desses seria a cópia local da matriz `ref_transicao_permitida` — exatamente o
 * defeito que o sistema anterior tinha, com a tela oferecendo o que o servidor recusava. O comando
 * sai sempre da resposta do servidor, procurado pelo DESTINO.
 *
 * A busca por destino é segura por construção, e não por inspeção: `ref_transicao_permitida` tem
 * `PRIMARY KEY (status_de, status_para)`, então a partir do estado atual cada destino tem no
 * máximo um comando. Não há empate a desfazer.
 */

/**
 * As zonas que NUNCA recebem um cartão solto, por mais que a matriz permita a transição.
 *
 * `APROVADA` é a Regra nº 1 do projeto virada código de tela: existe uma única linha da matriz
 * chegando lá (`DECIDIR_CAB_APROVAR`, do `CAB_PRESIDENTE`, com motivo obrigatório), e implantar
 * sem ela vale 10 pontos de IAD (ANEXO VIII, item 16). A decisão do comitê sai só do menu, lida
 * e nomeada, nunca de um gesto.
 *
 * **A trava é no DESTINO, e essa escolha é o ponto todo.** Travar a ORIGEM ("da coluna 3 não se
 * arrasta") fecharia só o caminho de hoje: a matriz cresce por INSERT — a V21 acrescentou três
 * linhas —, e uma linha futura chegando a `APROVADA` de outro `status_de` transformaria o arraste
 * no segundo caminho para a aprovação, em silêncio, sem quebrar teste nenhum.
 */
export const ZONAS_QUE_NUNCA_RECEBEM: StatusGmud[] = ['APROVADA']

/**
 * Esta zona pode receber um cartão solto?
 *
 * @param destino o estado que a zona representa
 * @returns falso quando soltar ali nunca é aceito
 */
export function zonaAceitaSoltar(destino: StatusGmud): boolean {
  return !ZONAS_QUE_NUNCA_RECEBEM.includes(destino)
}

/**
 * Qual ação do servidor leva a esta zona.
 *
 * @param acoes o que `/gmuds/{id}/transicoes-possiveis` devolveu para ESTE cartão, agora
 * @param destino o estado que a zona representa
 * @returns a ação correspondente, ou nulo quando a zona não é destino possível
 */
export function acaoParaZona(acoes: AcaoDisponivel[], destino: StatusGmud): AcaoDisponivel | null {
  if (!zonaAceitaSoltar(destino)) {
    return null
  }
  const acao = acoes.find((candidata) => candidata.statusDestino === destino)
  // A segunda checagem parece redundante com a primeira e não é: ela olha o destino da AÇÃO, e não
  // o da zona. Se um dia a resposta do servidor trouxer um destino que a zona não anuncia, quem
  // decide continua sendo a lista de zonas travadas.
  if (!acao || !zonaAceitaSoltar(acao.statusDestino)) {
    return null
  }
  return acao
}

/** O que viaja no arraste. Só o identificador: o dado do cartão já está na tela. */
const TIPO_DO_ARRASTE = 'text/plain'

/**
 * Marca o início do arraste de um cartão.
 *
 * O `setData` não é decoração: sem ele o Firefox nem começa o arraste. O identificador vai junto
 * porque é o que sobra caso o gesto termine numa janela que não conhece o estado desta tela.
 *
 * @param evento o evento nativo de arraste
 * @param gmudId identificador interno da solicitação arrastada
 */
export function iniciarArraste(evento: DragEvent<HTMLElement>, gmudId: number) {
  evento.dataTransfer.effectAllowed = 'move'
  evento.dataTransfer.setData(TIPO_DO_ARRASTE, String(gmudId))
}

/**
 * Autoriza (ou não) a zona a receber o cartão que está sobre ela.
 *
 * Sem `preventDefault` o navegador recusa o soltar sozinho, com o cursor de proibido — é assim que
 * a zona `APROVADA` se recusa antes de qualquer código nosso rodar.
 *
 * @param evento o evento nativo de arraste
 * @param aceita se esta zona recebe cartão
 */
export function permitirSoltar(evento: DragEvent<HTMLElement>, aceita: boolean) {
  if (!aceita) {
    return
  }
  evento.preventDefault()
  evento.dataTransfer.dropEffect = 'move'
}

/** O que o arraste propôs: este cartão, nesta zona. */
export interface PropostaDeArraste {
  gmudId: number
  destino: StatusGmud
  /** O nome da zona na tela, para dizer por extenso quando ela não é destino possível. */
  nomeDaZona: string
}

/**
 * Os fios que ligam cartão, zona e a região de anúncios — passados inteiros coluna abaixo.
 *
 * Um objeto só, e não sete props repetidas em quatro componentes: o cartão dispara o gesto, a zona
 * o recebe, e quem guarda o estado é o quadro. Espalhar isso em props soltas é como se perde um dos
 * fios no caminho, e o sintoma seria um arraste que não faz nada — sem erro nenhum na tela.
 */
export interface LigacoesDoArraste {
  /** O cartão que está na mão agora, ou nulo. */
  emArraste: number | null
  proposta: PropostaDeArraste | null
  aoPegar: (gmud: ResumoGmud) => void
  aoLargar: () => void
  aoSoltarNaZona: (destino: StatusGmud, nomeDaZona: string) => void
  aoFecharProposta: () => void
  /** Escreve na região `aria-live` do quadro. */
  aoAnunciar: (texto: string) => void
}
